import {
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  TextField,
  Button,
  Switch,
  Stack,
  Typography,
  Divider,
  FormControlLabel,
  MenuItem,
} from '../../../ui/core';
import { CloseIcon } from '../../../ui/icons';
import { useForm, useFormState } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '../../../hooks';
import {
  assignVehicleToTrip,
  attachFleetDriver,
  fetchVehicles,
} from '../../../redux/Fleet/actions';

const style = {
  fields: {
    padding: '32px',
    display: 'flex',
    gap: '16px',
    flexDirection: 'column',
  },
  payoutFields: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
  },
};

// payoutItemized components a manual attach can set; payout is their sum
// (server-side). Blank = 0.
const PAYOUT_FIELDS = [
  ['base', 'Base'],
  ['premier', 'Premier'],
  ['autoBonus', 'Auto Bonus'],
  ['extendedBonus', 'Extended Bonus'],
  ['manualBonus', 'Manual Bonus'],
  ['emptyReturn', 'Empty Return'],
  ['ghostLegOutbound', 'Ghost Leg Outbound'],
  ['ghostTripBonus', 'Ghost Trip Bonus'],
  ['waitTime', 'Wait Time'],
];

const amountRule = {
  validate: (v) =>
    v === '' || v == null || (!isNaN(Number(v)) && Number(v) >= 0) || 'Must be a number ≥ 0',
};

const emptyPayout = Object.fromEntries(PAYOUT_FIELDS.map(([f]) => [f, '']));

const toPayout = (values = {}) =>
  Object.fromEntries(
    PAYOUT_FIELDS.filter(([f]) => values[f] !== '' && values[f] != null).map(([f]) => [
      f,
      Number(values[f]),
    ]),
  );

const PayoutFields = ({ control, name, title }) => (
  <Box>
    <Typography variant="subtitle2" mb="8px">
      {title}
    </Typography>
    <Box sx={style.payoutFields}>
      {PAYOUT_FIELDS.map(([field, label]) => (
        <TextField
          key={field}
          label={label}
          control={control}
          name={`${name}.${field}`}
          rules={amountRule}
          size="small"
          type="number"
        />
      ))}
    </Box>
  </Box>
);

const AssignTripDialog = ({ onClose }) => {
  const dispatch = useDispatch();
  const t = useToast();
  const vehicles = useSelector((state) => state.fleet.vehicles.data);

  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      tripId: '',
      fleetVehicleId: '',
      withReturn: false,
      returnTripId: '',
      withDriver: false,
      driverId: '',
      payoutItemized: emptyPayout,
      returnPayoutItemized: emptyPayout,
    },
  });
  const { isDirty, isValid, isSubmitting } = useFormState({ control: form.control });
  const withReturn = form.watch('withReturn');
  const withDriver = form.watch('withDriver');

  const onSubmit = async (data) => {
    if (data.withDriver) {
      const { success, error } = await attachFleetDriver({
        outboundTripId: data.tripId.trim(),
        returnTripId: data.withReturn ? data.returnTripId.trim() : undefined,
        driverId: data.driverId.trim(),
        fleetVehicleId: data.fleetVehicleId,
        payoutItemized: toPayout(data.payoutItemized),
        returnPayoutItemized: data.withReturn
          ? toPayout(data.returnPayoutItemized)
          : undefined,
      })(dispatch);

      if (!success) {
        t.error(error || 'Failed to attach driver');
        return;
      }
      t.success(`Driver attached to ${data.withReturn ? 'both trips' : 'trip'}`);
      fetchVehicles()(dispatch);
      onClose();
      return;
    }

    const tripIds = [data.tripId.trim()];
    if (data.withReturn) tripIds.push(data.returnTripId.trim());
    for (const tripId of tripIds) {
      const { success } = await assignVehicleToTrip(tripId, data.fleetVehicleId)(dispatch);
      if (!success) {
        t.error(`Failed to assign vehicle to trip ${tripId}`);
        return;
      }
    }
    t.success('Vehicle assigned to trip');
    fetchVehicles()(dispatch);
    onClose();
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        padding="32px 32px 0 32px"
      >
        <DialogTitle sx={{ padding: 0 }}>Assign Vehicle to Trip</DialogTitle>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box component="form" onSubmit={form.handleSubmit(onSubmit)}>
        <Box sx={style.fields}>
          <TextField
            label="Trip ID (MongoDB)"
            control={form.control}
            name="tripId"
            rules={{ required: 'Required' }}
            fullWidth
          />
          <TextField
            label="Fleet Vehicle"
            control={form.control}
            name="fleetVehicleId"
            rules={{ required: 'Required' }}
            select
            fullWidth
          >
            {vehicles.map((v) => (
              <MenuItem key={v.id} value={v.id}>
                {v.displayName} {v.make && v.model ? `(${v.make} ${v.model})` : ''}
              </MenuItem>
            ))}
          </TextField>

          <Divider />

          <FormControlLabel
            label="Round trip (attach a return trip)"
            control={<Switch control={form.control} name="withReturn" />}
          />
          {withReturn && (
            <TextField
              label="Return Trip ID (MongoDB)"
              control={form.control}
              name="returnTripId"
              rules={{ required: withReturn ? 'Required' : false }}
              fullWidth
            />
          )}

          <FormControlLabel
            label="Attach driver manually"
            control={<Switch control={form.control} name="withDriver" />}
          />
          {withDriver && (
            <Stack spacing="16px">
              <Typography variant="caption" color="text.secondary">
                Fills the driver in and flips the trip(s) to finalizing_details, as a
                driver-accept would. Rides follow the driver; cancelled rides are skipped.
              </Typography>
              <TextField
                label="Driver ID (MongoDB)"
                control={form.control}
                name="driverId"
                rules={{ required: withDriver ? 'Required' : false }}
                fullWidth
              />
              <PayoutFields
                control={form.control}
                name="payoutItemized"
                title="Outbound payout (blank = 0)"
              />
              {withReturn && (
                <PayoutFields
                  control={form.control}
                  name="returnPayoutItemized"
                  title="Return payout (blank = 0)"
                />
              )}
            </Stack>
          )}
        </Box>
        <Button
          type="submit"
          disabled={!(isValid && isDirty)}
          loading={isSubmitting}
          variant="contained"
          size="large"
          disableElevation
          sx={{ width: '100%', padding: '20px', borderRadius: '0 0 4px 4px' }}
        >
          {withDriver ? 'Assign & Attach Driver' : 'Assign'}
        </Button>
      </Box>
    </Dialog>
  );
};

export default AssignTripDialog;
